// Leaf-level required-field lists per Commercial wizard step, keyed by the
// same step id strings used in both NewCommercialScreen.jsx and
// BackOfficeCommercial.jsx ('commercial_company', 'technical_data',
// 'personal_data'). Mirrors the answersFieldsBySteps pattern used in
// RcVehicle.validations.js.
//
// These are leaf dot-paths (not the shallow Object.keys(schema.fields)
// top-level keys) because react-hook-form's touchedFields only gets set
// per exact path — a nested schema key like 'technical_location_data'
// won't mark 'technical_location_data.annoc_value' as touched.
export const commercialStepFieldsById = {
  commercial_company: [
    'vat_number',
    'attpr_value',
    'turnover_value',
    'number_of_employees',
    'effective_date',
    'payment_frequency',
    'payment_frequency_value',
    'address.address',
    'address.house_number',
    'address.postal_code',
    'address.municipality_name',
    'address.province_code',
    'address.ministerial_municipality_code',
  ],
  technical_data: [
    'technical_location_data.annoc_value',
    'technical_location_data.annor_value',
    'technical_location_data.tpab_value',
    'technical_location_data.attpr_value',
    'property_value',
    // Kept here for trigger()/blocking-message purposes only — never
    // touched via setValue (it's a useFieldArray; see commercialStepFields
    // consumers). Its own red/green feedback is handled reactively by
    // GuaranteeValidationSummary, independent of RHF's errors/touched.
    'coverages',
  ],
  personal_data: [
    'gender',
    'company_name',
    'phone',
    'email',
  ],
};

// Fields that must never be marked touched via setValue (see
// commercialStepFieldsById.technical_data comment above).
export const NON_TOUCHABLE_FIELDS = ['coverages'];

export const commercialFieldLabels = {
  vat_number: 'Partita IVA',
  attpr_value: 'Attività Principale',
  turnover_value: 'Valore del Fatturato',
  number_of_employees: 'Numero di Dipendenti',
  effective_date: 'Data di Decorrenza',
  payment_frequency: 'Frequenza di Pagamento',
  payment_frequency_value: 'Frequenza di Pagamento',
  'address.address': 'Indirizzo',
  'address.house_number': 'Numero Civico',
  'address.postal_code': 'CAP',
  'address.municipality_name': 'Comune',
  'address.province_code': 'Provincia',
  'address.ministerial_municipality_code': 'Codice Comune Ministeriale',
  'technical_location_data.annoc_value': 'Anno di Costruzione',
  'technical_location_data.annor_value': 'Anno di Ricostruzione',
  'technical_location_data.tpab_value': 'Tipo Fabbricato',
  'technical_location_data.attpr_value': 'Attività Principale',
  property_value: 'Valore Immobile',
  coverages: 'Garanzie',
  gender: 'Genere',
  company_name: 'Ragione Sociale',
  phone: 'Telefono',
  email: 'Email',
};

// Dot-path aware getter, mirrors the getFieldByPath already used inline in
// NewCommercialScreen.jsx.
export function getFieldByPath(obj, path) {
  return path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
}

// The one correct isValid implementation — requires both "no error" and
// "touched", dot-path aware so it works for nested fields in both the
// public and BackOffice wizards.
export function isCommercialFieldValid(errors, touchedFields, name) {
  if (getFieldByPath(errors, name)?.message !== undefined) {
    return false;
  }
  if (getFieldByPath(touchedFields, name) === undefined) {
    return false;
  }
  return true;
}

// Synchronous, independent of react-hook-form's (async) internal state
// update timing — `trigger()`'s returned promise can resolve before RHF's
// own setState has actually re-rendered the component, so reading the
// component's `errors` closure right after `await trigger(...)` can be
// stale. Re-running the same yup schema against the same formData snapshot
// used for `schema.isValidSync(formData)` sidesteps that entirely.
export function getBlockingFields(schema, formData, stepFields) {
  try {
    schema.validateSync(formData, { abortEarly: false });
    return [];
  } catch (err) {
    const errorPaths = new Set((err.inner || []).map((innerError) => innerError.path));
    return (stepFields || []).filter((field) => errorPaths.has(field));
  }
}

export function buildMissingFieldsMessage(blockingFields) {
  if (!blockingFields || blockingFields.length === 0) {
    return 'Per favore, completa i campi richiesti correttamente.';
  }

  const names = blockingFields.map((field) => commercialFieldLabels[field] || field);
  const shown = names.slice(0, 3).join(', ');
  const rest = names.length > 3 ? ` e altri ${names.length - 3}` : '';

  return `Per favore, completa correttamente i seguenti campi: ${shown}${rest}.`;
}
